import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/** Rodzaje danych, dla których kopiowanie ma z góry ustalony komunikat i podpowiedź. */
export type CopyKind = 'imei' | 'email' | 'phone' | 'location';

interface CopyKindLabels {
  /** Klucz komunikatu pokazywanego po skopiowaniu. */
  message: string;
  /** Klucz podpowiedzi na afordancji (title / aria-label). */
  tooltip: string;
  /** Parametr, pod którym wartość wchodzi do komunikatu; brak = komunikat bez parametrów. */
  param?: string;
}

/**
 * Jedno miejsce, które wie, jak nazwać skopiowaną wartość. Korzystają z niego
 * <app-copy-button> i [appCopyOnClick] — gdyby każdy miał własną kopię tej mapy,
 * wróciłyby dwa źródła prawdy.
 */
const COPY_KINDS: { [kind in CopyKind]: CopyKindLabels } = {
  imei:     { message: 'COMMON.IMEI_COPIED',     tooltip: 'COMMON.COPY_IMEI',     param: 'imei' },
  email:    { message: 'COMMON.EMAIL_COPIED',    tooltip: 'COMMON.COPY_EMAIL',    param: 'email' },
  phone:    { message: 'COMMON.PHONE_COPIED',    tooltip: 'COMMON.COPY_PHONE',    param: 'phone' },
  location: { message: 'COMMON.LOCATION_COPIED', tooltip: 'COMMON.COPY_LOCATION' },
};

@Injectable({ providedIn: 'root' })
export class ClipboardService {

  constructor(private translate: TranslateService) {}

  /**
   * Czy wartość w ogóle nadaje się do schowka. Publiczne, bo afordancje decydują
   * na tej samej podstawie, czy się w ogóle wyrenderować — inaczej ta sama reguła
   * żyłaby osobno w każdym szablonie.
   */
  isCopyable(text: string | number): boolean {
    // Myślnik to nie wartość, tylko to, co szablony rysują w miejsce braku danych.
    // Skopiowanie go dałoby użytkownikowi „-" w schowku i potwierdzenie sukcesu.
    return text !== null && text !== undefined && text !== '' && text !== '-';
  }

  /**
   * Zwraca, czy wartość faktycznie trafiła do schowka. Afordancje potwierdzają
   * dopiero na tej podstawie — ptaszek „na wiarę" byłby tym samym kłamstwem, co
   * toast sukcesu przy nieudanym execCommand.
   */
  copy(text: string | number, message?: string, event?: Event): Promise<boolean> {
    if (event) {
      event.stopPropagation();
    }
    if (!this.isCopyable(text)) {
      return Promise.resolve(false);
    }
    const value = String(text);

    if (!navigator.clipboard) {
      // Ścieżka zapasowa rozstrzyga się synchronicznie, więc nie odraczamy jej sztucznie.
      return Promise.resolve(this.announce(this.fallbackCopy(value, null), message));
    }
    // Dwuargumentowy then, a nie .catch: inaczej ścieżka zapasowa odpalałaby się
    // także wtedy, gdy to pokazanie tosta rzuciło wyjątkiem.
    return navigator.clipboard.writeText(value).then(
      () => this.announce(true, message),
      apiFailure => this.announce(this.fallbackCopy(value, apiFailure), message)
    );
  }

  /** Pokazuje użytkownikowi wynik operacji i przekazuje go dalej wywołującemu. */
  private announce(copied: boolean, message?: string): boolean {
    if (copied) {
      if (message) {
        this.showToast(message);
      }
      return true;
    }
    // Cisza byłaby tu gorsza niż błąd: użytkownik wkleiłby poprzednią zawartość
    // schowka, przekonany, że wkleja to, co przed chwilą „skopiował".
    this.showToast(this.translate.instant('COMMON.COPY_ERROR'));
    return false;
  }

  /** Kopiowanie z komunikatem i parametrem dobranymi po rodzaju danych. */
  copyAs(text: string | number, kind: CopyKind, event?: Event): Promise<boolean> {
    return this.copy(text, this.messageFor(kind, text), event);
  }

  /** Przetłumaczona podpowiedź afordancji dla danego rodzaju. */
  tooltipFor(kind: CopyKind): string {
    const labels = this.labelsFor(kind);
    return labels ? this.translate.instant(labels.tooltip) : '';
  }

  private messageFor(kind: CopyKind, text: string | number): string | undefined {
    const labels = this.labelsFor(kind);
    if (!labels) {
      // Brak komunikatu, a nie surowy klucz na ekranie: wartość i tak trafia do
      // schowka, bo to jest to, po co użytkownik kliknął.
      return undefined;
    }
    return labels.param
      ? this.translate.instant(labels.message, { [labels.param]: String(text) })
      : this.translate.instant(labels.message);
  }

  /**
   * Wygląda na strażnicę nie do przejścia, bo typ CopyKind jej zabrania — ale
   * szablony w widokach osadzonych (*ngIf / *ngFor) nie są typowane, więc literówka
   * w copyKind="imie" przechodzi build i dociera tutaj. Musi być słyszalna.
   */
  private labelsFor(kind: CopyKind): CopyKindLabels {
    const labels = COPY_KINDS[kind];
    if (labels) {
      return labels;
    }
    console.error(
      `ClipboardService: nieznany rodzaj kopiowania "${kind}". Dozwolone: ${Object.keys(COPY_KINDS).join(', ')}`
    );
    return null;
  }

  /** Ścieżka zapasowa dla przeglądarek bez Clipboard API. Zwraca, czy się udało. */
  private fallbackCopy(text: string, apiFailure: any): boolean {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);

    let copied = false;
    let failure: any = null;
    try {
      textarea.select();
      copied = document.execCommand('copy');
    } catch (error) {
      failure = error;
    } finally {
      // W finally, bo execCommand potrafi rzucić — inaczej pole zostawałoby w DOM.
      document.body.removeChild(textarea);
    }

    if (!copied) {
      console.error('ClipboardService: nie udało się skopiować do schowka', failure || apiFailure);
    }
    return copied;
  }

  private showToast(message: string) {
    const toast = document.createElement('div');
    toast.textContent = message;
    Object.assign(toast.style, {
      position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
      background: '#333', color: '#fff', padding: '10px 24px', borderRadius: '8px',
      fontSize: '14px', zIndex: '10000', opacity: '0', transition: 'opacity 0.3s'
    });
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.style.opacity = '1');
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  }
}
